import { enqueue } from './behaviorTracker';
import { matchGenerationStart, toRelativeApiPath } from './generationSignals';

/**
 * axios のインターセプタから呼ばれる API シグナル。
 *
 * - api_error   … status >= 400（401 のトークン更新経路は除外）
 * - api_timing  … 所要 SLOW_API_MS 超の成功呼び出し
 * - generation_started … generationSignals の表に一致した POST
 *
 * 生成の完了/失敗はサーバー側（GenerationOutcomeRecorder）が記録する。ここで
 * ポーリングのレスポンスから拾うと、画面を閉じた時や API 障害で丸ごと欠落する。
 *
 * パスは数値・UUID・ハッシュを :id に置換して集計しやすくする。
 */

export const SLOW_API_MS = 3000;

const isIdLike = (segment: string): boolean =>
  /^\d+$/.test(segment) ||
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment) ||
  /^[0-9a-f]{12,}$/i.test(segment) ||
  /^[A-Za-z0-9_-]{20,}$/.test(segment);

export const normalizeApiPath = (url: string): string =>
  toRelativeApiPath(url)
    .split('/')
    .map((segment) => (isIdLike(segment) ? ':id' : segment))
    .join('/');

const currentPathname = (): string =>
  typeof window !== 'undefined' ? window.location.pathname : '/';

export const onApiRequest = (method: string, url: string): void => {
  const type = matchGenerationStart(method, url, currentPathname());
  if (!type) return;
  enqueue({
    event_type: 'action',
    event_name: 'generation_started',
    props: { type, path: normalizeApiPath(url) },
  });
};

export const onApiResponse = (method: string, url: string, status: number, durationMs: number): void => {
  const upper = method.toUpperCase();
  const path = normalizeApiPath(url);

  if (durationMs > SLOW_API_MS) {
    enqueue({
      event_type: 'api_timing',
      event_name: `${upper} ${path}`,
      duration_ms: Math.round(durationMs),
      props: { method: upper, path, status },
    });
  }
};

export const onApiError = (
  method: string,
  url: string,
  status: number | undefined,
  durationMs: number
): void => {
  if (status === 401) return;
  const upper = method.toUpperCase();
  const path = normalizeApiPath(url);
  enqueue({
    event_type: 'api_error',
    event_name: `${upper} ${path}`,
    duration_ms: Math.round(durationMs),
    props: { method: upper, path, status: status ?? 0 },
  });
};
